import React from "react";
import { observer } from "mobx-react-lite";
import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Drawer,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useCartStore } from "../../stores/CartStore";
import CartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import CartRow from "./CartRow";
import ASCartRow from "./ASCartRow";
import DialogStore from "../../stores/DialogStore";
import { IsMobile, priceFormatter } from "../../utils/helpers";
import Link from "next/link";
import UICartStore from "../../stores/UICartStore";
import NothingHereYet from "../NothingHereYet";
import CartExpiryBanner from "./CartExpiryBanner";
import toast from "../../utils/toast";
import SeatmapCartRow from "./SeatmapCartRow";
import PackageCartRow from "./PackageCartRow";
import InfoBubble from "../common/InfoBubble";
import { useRouter } from "next/router";

type Props = {
    baseUrl?: string;
    disablePromoCode?: boolean;
};

const Cart = observer(({ baseUrl, disablePromoCode = false }: Props) => {
    const [promoCode, setPromoCode] = React.useState("");
    const [redemptionCode, setRedemptionCode] = React.useState("");

    const router = useRouter();

    const cartStore = useCartStore();

    const onClearCart = () => {
        if (cartStore?.quantity === 0) return;
        DialogStore.showAlert(
            "Clear Cart",
            "Are you sure you want to clear your cart? Some tickets may no longer be available to purchase.",
            () => cartStore?.clearCart(),
        );
    };

    const subtotal = cartStore?.subtotal ?? 0;
    const authFee = cartStore?.authFee ?? 0;
    const total = cartStore?.total ?? 0;

    const showSubtotal = subtotal !== total;
    const hasItems = !!cartStore?.hasItems;

    return (
        <>
            <IconButton
                onClick={() => UICartStore.setOpen(true)}
                sx={(theme) => ({
                    position: "fixed",
                    right: 10,
                    top: 10,
                    border: `1px solid ${theme.palette.primary.main}`,
                    backgroundColor: `${theme.palette.primary.main}20`,
                })}
                color="primary"
            >
                <Badge badgeContent={cartStore?.quantity} color="info">
                    <CartIcon />
                </Badge>
            </IconButton>
            <Drawer
                variant={IsMobile() ? "temporary" : "persistent"}
                anchor="right"
                open={UICartStore.open}
                onClose={() => UICartStore.setOpen(false)}
                sx={{
                    width: "500px",
                    maxWidth: "100%",
                    maxHeight: "var(--app-height)",
                }}
                PaperProps={{
                    sx: {
                        width: "500px",
                        maxWidth: "100%",
                        maxHeight: "100%",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                        p: 1,
                    }}
                >
                    {cartStore?.newestItemDate && (
                        <Box sx={{ mb: 1 }}>
                            <CartExpiryBanner
                                time={cartStore.newestItemDate}
                                onTimeUp={() => {
                                    toast.error("Cart has expired.");
                                    cartStore.clearCart(false);
                                }}
                            />
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <IconButton onClick={() => UICartStore.setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                        <Button
                            onClick={onClearCart}
                            variant="contained"
                            color="error"
                            disabled={cartStore?.quantity === 0}
                        >
                            Clear Cart
                        </Button>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ flexGrow: 1, overflowY: "auto", p: 0.5 }}>
                        <Grid container spacing={2}>
                            {cartStore?.hasRequiredEvents &&
                                cartStore.requiredEvents.map((it) => (
                                    <Grid
                                        item
                                        xs={12}
                                        key={`required-event-${it.id}`}
                                    >
                                        <Alert severity="info">
                                            <b>{it.title}</b> does NOT include
                                            admission to the event. Please
                                            ensure you have the appropriate
                                            number of admissions for{" "}
                                            <Link
                                                href={`/store/${it.requiredEvent?.client.cartUrl}/${it.requiredEvent?.id}`}
                                            >
                                                <b
                                                    style={{
                                                        textDecoration:
                                                            "underline",
                                                        color: "lightblue",
                                                    }}
                                                >
                                                    {it.requiredEvent?.title}
                                                </b>
                                            </Link>{" "}
                                            in your cart before checking out!
                                        </Alert>
                                    </Grid>
                                ))}
                            {cartStore?.packageItemsGrouped.map((it) => (
                                <Grid
                                    item
                                    xs={12}
                                    key={`${it.eventPackage.id}-${it.items
                                        .map((it2) => it2.id)
                                        .join("-")}`}
                                >
                                    <PackageCartRow
                                        row={it}
                                        cartStore={cartStore}
                                    />
                                </Grid>
                            ))}
                            {cartStore?.seatmapItemsGrouped.map((it) => (
                                <Grid item xs={12} key={it.eventSeatmap.id}>
                                    <SeatmapCartRow
                                        row={it}
                                        cartStore={cartStore}
                                    />
                                </Grid>
                            ))}
                            {cartStore?.assignedSeatingItemsGrouped.map(
                                (it) => (
                                    <Grid
                                        item
                                        xs={12}
                                        key={it.items
                                            .map((it2) => it2.ticketId)
                                            .join("-")}
                                    >
                                        <Card>
                                            <CardContent>
                                                <Typography
                                                    variant="body2"
                                                    color="primaryText.main"
                                                    fontWeight={600}
                                                >
                                                    {it.department.name}
                                                </Typography>
                                                <Divider sx={{ my: 2 }} />
                                                <Grid container spacing={2}>
                                                    {it.items.map(
                                                        (it2, idx2) => (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                key={
                                                                    it2.ticketId
                                                                }
                                                            >
                                                                <ASCartRow
                                                                    item={it2}
                                                                    onRemove={() =>
                                                                        cartStore?.removeTicketsFromCart(
                                                                            [
                                                                                it2.ticketId,
                                                                            ],
                                                                        )
                                                                    }
                                                                    cartStore={
                                                                        cartStore
                                                                    }
                                                                    onRemoveRedemptionCode={() => {
                                                                        if (
                                                                            !it2.redemption
                                                                        )
                                                                            return;
                                                                        cartStore.removeRedemptionCode(
                                                                            it2
                                                                                .redemption
                                                                                ?.id,
                                                                        );
                                                                    }}
                                                                />
                                                                {idx2 !==
                                                                    it.items
                                                                        .length -
                                                                        1 && (
                                                                    <Divider
                                                                        sx={{
                                                                            mt: 2,
                                                                        }}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        ),
                                                    )}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ),
                            )}
                            {cartStore?.gaItems.map((it) => (
                                <Grid item xs={12} key={it.ticketIds.join("-")}>
                                    <CartRow
                                        item={it}
                                        onIncrement={() =>
                                            cartStore?.incrementGaQuantity(
                                                it.department.id,
                                                it.comp,
                                                it.department,
                                                it.redemption?.id,
                                            )
                                        }
                                        onDecrement={() =>
                                            cartStore?.decrementGaQuantity(
                                                it.department.id,
                                                it.comp,
                                                it.department,
                                                it.redemption?.id,
                                            )
                                        }
                                        onSetQuantity={(qty) =>
                                            cartStore?.setGaQuantity(
                                                it.department.id,
                                                it.comp,
                                                qty,
                                                it.department,
                                                it.redemption?.id,
                                            )
                                        }
                                        onRemove={() =>
                                            cartStore?.setGaQuantity(
                                                it.department.id,
                                                it.comp,
                                                0,
                                                it.department,
                                                it.redemption?.id,
                                            )
                                        }
                                        onRemoveRedemptionCode={() => {
                                            if (!it.redemption) return;
                                            cartStore.removeRedemptionCode(
                                                it.redemption?.id,
                                            );
                                        }}
                                    />
                                </Grid>
                            ))}
                            {!cartStore?.hasItems && (
                                <Grid item xs={12}>
                                    <NothingHereYet message="Your cart is empty." />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ mb: 1 }}>
                        {cartStore?.shouldShowPromoCodeForm &&
                            !disablePromoCode && (
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (!cartStore?.promoCode) {
                                            setPromoCode("");
                                            cartStore
                                                ?.applyPromoCode(promoCode)
                                                .then(() => {
                                                    router.replace(
                                                        router.asPath,
                                                    );
                                                })
                                                .catch(() => {});
                                        } else {
                                            cartStore
                                                ?.removePromoCode()
                                                .then(() => {
                                                    router.replace(
                                                        router.asPath,
                                                    );
                                                })
                                                .catch(() => {});
                                        }
                                    }}
                                >
                                    <Grid container spacing={1} sx={{ px: 1 }}>
                                        <Grid item xs={8}>
                                            <TextField
                                                label="Promo Code"
                                                fullWidth
                                                size="small"
                                                color="primary"
                                                value={
                                                    cartStore?.promoCode ||
                                                    promoCode
                                                }
                                                disabled={
                                                    !!cartStore?.promoCode ||
                                                    cartStore?.loading
                                                }
                                                onChange={(e) => {
                                                    setPromoCode(
                                                        e.target.value,
                                                    );
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Button
                                                variant="contained"
                                                color={
                                                    cartStore?.promoCode
                                                        ? "error"
                                                        : "primary"
                                                }
                                                fullWidth
                                                type="submit"
                                                disabled={cartStore?.loading}
                                            >
                                                {cartStore?.promoCode
                                                    ? "Clear"
                                                    : "Apply"}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        {cartStore?.shouldShowRedemptionCodeForm && (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!redemptionCode) return;
                                    cartStore?.applyRedemptionCode(
                                        redemptionCode,
                                    );
                                    setRedemptionCode("");
                                }}
                                style={{ marginTop: 5 }}
                            >
                                <Grid container spacing={1} sx={{ px: 1 }}>
                                    <Grid item xs={8}>
                                        <TextField
                                            label="Redemption Code"
                                            fullWidth
                                            size="small"
                                            color="primary"
                                            value={redemptionCode}
                                            disabled={cartStore?.loading}
                                            onChange={(e) => {
                                                setRedemptionCode(
                                                    e.target.value,
                                                );
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            type="submit"
                                            disabled={
                                                !redemptionCode ||
                                                cartStore?.loading
                                            }
                                        >
                                            Apply
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                        <Grid container spacing={1} sx={{ mt: 1, px: 1 }}>
                            {authFee !== 0 && (
                                <>
                                    <Grid item xs={8}>
                                        <Typography variant="body2">
                                            Transaction Fee
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                        >
                                            {priceFormatter(authFee)}
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            {cartStore?.deliveryMethod && (
                                <>
                                    <Grid item xs={8}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "3px",
                                            }}
                                        >
                                            {cartStore.deliveryMethod.name}
                                            <InfoBubble title="More delivery options may be available at checkout." />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                        >
                                            {priceFormatter(
                                                cartStore.deliveryMethod.amount,
                                            )}
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            {showSubtotal && (
                                <>
                                    <Grid item xs={8}>
                                        <Typography variant="body2">
                                            Subtotal
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                        >
                                            {priceFormatter(subtotal)}
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            {cartStore?.taxes.map((it) => (
                                <>
                                    <Grid item xs={8}>
                                        <Typography variant="body2">
                                            {it.name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="body2"
                                            textAlign="right"
                                        >
                                            {priceFormatter(it.amount)}
                                        </Typography>
                                    </Grid>
                                </>
                            ))}
                            <Grid item xs={8}>
                                <Typography fontWeight={900}>Total</Typography>
                            </Grid>
                            <Grid item xs={4} textAlign="right">
                                <Typography>{priceFormatter(total)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                {hasItems ? (
                                    <Link
                                        href={`${
                                            baseUrl ??
                                            `/store/${cartStore?.discriminator}`
                                        }/checkout`}
                                    >
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={!hasItems}
                                        >
                                            Proceed to Checkout
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={!hasItems}
                                    >
                                        Proceed to Checkout
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
});

export default Cart;
